// src/components/network/ForfeoCheckoutNetworkPanel.tsx
import React from 'react';
import { CalendarCheck, CheckCircle2, QrCode, ShieldCheck, Smartphone, WalletCards } from 'lucide-react';

type ForfeoCheckoutNetworkPanelProps = {
  variant?: 'checkout' | 'booking' | 'success';
  compact?: boolean;
};

const CONTENT = {
  checkout: {
    badge: 'Paiement protégé Forfeo',
    title: 'Votre achat prépare automatiquement votre pass Forfeo.',
    description:
      'Après confirmation, l’expérience pourra être retrouvée dans vos achats et préparée pour le wallet mobile, le QR sécurisé et la validation partenaire.',
    items: [
      ['Paiement suivi', 'Reçu et confirmation Forfeo'],
      ['Wallet mobile', 'Pass prêt pour l’application'],
      ['QR sécurisé', 'Validation partenaire sans double usage'],
    ],
  },
  booking: {
    badge: 'Réservation assistée Forfeo',
    title: 'Choisissez votre créneau avec une expérience plus claire sur mobile.',
    description:
      'Forfeo garde la réservation lisible, sécurisée et compatible avec le suivi client, les confirmations et les futurs pass expérience.',
    items: [
      ['Créneau clair', 'Calendrier lisible sur mobile'],
      ['Confirmation', 'Suivi après réservation'],
      ['Partenaire officiel', 'Expérience associée au réseau Forfeo'],
    ],
  },
  success: {
    badge: 'Pass expérience en préparation',
    title: 'Votre réservation entre dans l’écosystème Forfeo.',
    description:
      'La prochaine étape sera de retrouver votre réservation, son statut, son QR et son pass dans votre espace client et dans l’application mobile Forfeo.',
    items: [
      ['Réservation confirmée', 'Historique client Forfeo'],
      ['QR à venir', 'Validation auprès du partenaire'],
      ['Support Forfeo', 'Aide en cas de question'],
    ],
  },
} as const;

export default function ForfeoCheckoutNetworkPanel({
  variant = 'checkout',
  compact = false,
}: ForfeoCheckoutNetworkPanelProps) {
  const c = CONTENT[variant];

  return (
    <section className={`rounded-[28px] border border-blue-100 bg-gradient-to-br from-white via-blue-50/70 to-cyan-50/70 shadow-sm ${compact ? 'p-4' : 'p-5 md:p-6'}`}>
      <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-blue-700 shadow-sm">
            <ShieldCheck className="h-3.5 w-3.5" />
            {c.badge}
          </div>
          <h3 className="text-lg md:text-xl font-black tracking-tight text-slate-950">
            {c.title}
          </h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
            {c.description}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 md:min-w-[260px]">
          <div className="rounded-2xl bg-white/85 p-3 text-center shadow-sm ring-1 ring-slate-100">
            <WalletCards className="mx-auto mb-2 h-5 w-5 text-blue-600" />
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Wallet</div>
          </div>
          <div className="rounded-2xl bg-white/85 p-3 text-center shadow-sm ring-1 ring-slate-100">
            <QrCode className="mx-auto mb-2 h-5 w-5 text-emerald-600" />
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">QR</div>
          </div>
          <div className="rounded-2xl bg-white/85 p-3 text-center shadow-sm ring-1 ring-slate-100">
            <Smartphone className="mx-auto mb-2 h-5 w-5 text-purple-600" />
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">App</div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {c.items.map(([title, text]) => (
          <div key={title} className="flex items-start gap-3 rounded-2xl bg-white/75 p-3 ring-1 ring-slate-100">
            {variant === 'booking' ? (
              <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            ) : (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            )}
            <div>
              <div className="text-xs font-black text-slate-900">{title}</div>
              <div className="text-[11px] font-semibold leading-snug text-slate-500">{text}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
